import React, { ReactElement, useState, useContext } from 'react';
import './FovMarker.scss';
import { AppContext } from '../../../../state/app.state';
import { useObserver } from 'mobx-react-lite';
import { CoordinatesT } from '../../../../state/app.state.types';


type Props = {
    rotation: number;
    scale: number;
    fov: number;
    focused: boolean;
    viewId: number;
    relativePosition: CoordinatesT;
    onClick?: (event: React.MouseEvent<SVGElement>) => void;
};

const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number): CoordinatesT => {
    const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;

    return {
        x: centerX + (radius * Math.cos(angleInRadians)),
        y: centerY + (radius * Math.sin(angleInRadians))
    };
};

const describeArc = (x: number, y: number, radius: number, startAngle: number, endAngle: number): string => {

    const start = polarToCartesian(x, y, radius, endAngle);
    const end = polarToCartesian(x, y, radius, startAngle);

    const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';

    const d = [
        'M', start.x, start.y,
        'A', radius, radius, 0, largeArcFlag, 0, end.x, end.y,
        'L', start.x, start.y,
        'L', end.x, end.y,
        'L 0 0 Z'
    ].join(' ');

    return d;
};

const FovMarker: React.FC<Props> = ({ focused, rotation, scale, fov, relativePosition, onClick }): ReactElement => {
    const { presentationState } = useContext(AppContext);
    const fovRotationOffset = 135; //The constant rotation offset of the fov is 90+45 degrees.
    const [hover, setHover] = useState(false);
    const [stopAnimation, setStopAnimation] = useState(false);

    const animate = (value: boolean) => ({ animation: value ? 'unset' : 'fade 1.5s ease-in' });

    return useObserver(() => (
        <svg className={'bv-fov-marker'}
            width='100'
            height='100'
            viewBox='0 0 100 100'
            xmlns='http://www.w3.org/2000/svg'
            style={{
                transform:[`rotate(${ rotation - fovRotationOffset }deg)`,
                    `scale(${ scale })`].join(' '),
                left: `${relativePosition.x }%`,
                top: `${relativePosition.y }%`
            }}
        >
            <defs>
                <radialGradient id={'bv-Gradient-active'} cx='0' cy='0' r='1' fx='0' fy='0'>
                    <stop style={{ animation: 'fade-active 1.5s ease-in', animationDelay: '2s', animationIterationCount: 'infinite' }} className={'bv-stop bv-stop1 '} offset='99%' />
                    <stop style={{ animation: 'fade-active 1.5s ease-in', animationDelay: '2s', animationIterationCount: 'infinite' }} className={'bv-stop bv-stop3'} offset='100%' />
                </radialGradient>
                <radialGradient id={'bv-Gradient-hover'} cx='0' cy='0' r='1' fx='0' fy='0'>
                    <stop style={ animate(!!presentationState?.showOnboarding)} className={'bv-stop-hover bv-stop1 '} offset='99%' />
                    <stop style={ animate(!!presentationState?.showOnboarding)} className={'bv-stop-hover bv-stop3'} offset='100%' />
                </radialGradient>
                <radialGradient id={'bv-Gradient-inactive'} cx='0' cy='0' r='1' fx='0' fy='0'>
                    <stop style={ animate(!!presentationState?.showOnboarding)} className={'bv-stop-inactive bv-stop1 '} offset='99%' />
                    <stop style={ animate(!!presentationState?.showOnboarding)} className={'bv-stop-inactive bv-stop3'} offset='100%' />
                </radialGradient>
            </defs>
            <g className='bv-g1' onClick={onClick}
                onMouseOver={(): void => setHover(true)}
                onMouseOut={(): void => setHover(false)}>
                <path d={describeArc(0, 0, 75, (fovRotationOffset - (fov / 2)), (fovRotationOffset + (fov / 2)))} fill={`url(#bv-Gradient${(focused ? '-active' : hover ? '-hover' : '-inactive')})`} />
            </g>
        </svg>
    ));
};


export default FovMarker;